import { useEffect, useRef, useState, Children } from "react";
import { Link } from "react-router-dom";
import { api, apiError, downloadFile } from "@/lib/api";
import { uploadSmart } from "@/lib/upload";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { formatDate, FileChips, UploadProgress, dropProps, trFold, statusesForZone, ALL_STATUSES, statusColorCls, SearchClear, hubsForZone, useSaleStatuses, saleStatusCls, setSaleStatusesCache, prodStatusCls, OfferAmountCell, KpiCard, areaNum } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Trash2, Eye, Pencil, Paperclip, Send, MoreHorizontal, FolderOpen, Ruler, UserCheck, Box, Boxes, ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, Download, FileSpreadsheet, Upload, ChevronLeft, ChevronRight, Columns3 } from "lucide-react";
import { Document, Page } from "react-pdf";
import { ProposalDialog } from "@/components/ProposalDialog";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const zoneCls = {
  "Blue Zone": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Green Zone": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  "Ofisler": "bg-orange-500/10 text-orange-600 border-orange-500/20",
};

const isImgFile = (f) => (f.content_type || "").startsWith("image/");

export const useHiddenCols = (storageKey) => {
  const [hidden, setHidden] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || []; } catch { return []; }
  });
  const update = (h) => { setHidden(h); try { localStorage.setItem(storageKey, JSON.stringify(h)); } catch {} };
  return [hidden, update];
};

export const useColOrder = (storageKey, defaultKeys) => {
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey)) || [];
      const valid = saved.filter((k) => defaultKeys.includes(k));
      const merged = [...valid];
      defaultKeys.forEach((k, i) => {
        if (merged.includes(k)) return;
        const prev = defaultKeys.slice(0, i).reverse().find((x) => merged.includes(x));
        merged.splice(prev ? merged.indexOf(prev) + 1 : 0, 0, k);
      });
      return merged;
    } catch {
      return defaultKeys;
    }
  });
  const update = (o) => { setOrder(o); try { localStorage.setItem(storageKey, JSON.stringify(o)); } catch {} };
  return [order, update];
};

export const OrderedCells = ({ order, children }) => {
  const map = {};
  Children.forEach(children, (c) => { if (c && c.key != null) map[c.key] = c; });
  return <>{order.map((k) => map[k] || null)}</>;
};

export const useResizableCols = (tableRef, storageKey) => {
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    let widths = {};
    try { widths = JSON.parse(localStorage.getItem(storageKey)) || {}; } catch {}
    const save = () => { try { localStorage.setItem(storageKey, JSON.stringify(widths)); } catch {} };
    const cleanups = [];
    table.querySelectorAll("thead th").forEach((th) => {
      const key = (th.textContent || "").trim();
      if (!key) return;
      if (widths[key]) { th.style.width = widths[key] + "px"; th.style.minWidth = widths[key] + "px"; }
      if (th.querySelector(".col-resizer")) return;
      th.style.position = "relative";
      const h = document.createElement("span");
      h.className = "col-resizer";
      h.style.cssText = "position:absolute;top:0;right:0;width:7px;height:100%;cursor:col-resize;user-select:none;z-index:5;";
      h.onmouseenter = () => { h.style.background = "#94a3b8"; };
      h.onmouseleave = () => { h.style.background = ""; };
      h.addEventListener("mousedown", (e) => {
        e.preventDefault(); e.stopPropagation();
        const startX = e.clientX, startW = th.offsetWidth;
        const onMove = (ev) => {
          const w = Math.max(56, startW + ev.clientX - startX);
          th.style.width = w + "px"; th.style.minWidth = w + "px";
          widths[key] = w;
        };
        const onUp = () => { save(); document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
      });
      th.appendChild(h);
      cleanups.push(() => h.remove());
    });
    return () => cleanups.forEach((f) => f());
  });
};

const PIN_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17v5"/><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1z"/></svg>';

export const usePinnedCols = (tableRef, storageKey) => {
  const [pinned, setPinned] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || []; } catch { return []; }
  });
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const ths = Array.from(table.querySelectorAll("thead th"));
    const rows = Array.from(table.querySelectorAll("tbody tr"));
    const label = (th) => {
      const b = th.querySelector("button");
      return ((b ? b.textContent : th.textContent) || "").trim();
    };
    const visibleCells = (tr) => Array.from(tr.cells).filter((c) => !c.classList.contains("hidden"));
    const clear = (el) => {
      el.style.position = el.classList.contains("col-pin-host") ? "relative" : "";
      el.style.left = ""; el.style.zIndex = ""; el.style.backgroundColor = ""; el.style.boxShadow = "";
    };
    ths.forEach((th, i) => {
      clear(th);
      rows.forEach((tr) => { const c = visibleCells(tr)[i]; if (c) clear(c); });
    });
    const cleanups = [];
    ths.forEach((th) => {
      const key = label(th);
      if (!key || key === "#" || key === "İŞLEM" || key === "İşlem") return;
      th.classList.add("col-pin-host");
      let btn = th.querySelector(".col-pin");
      if (!btn) {
        btn = document.createElement("span");
        btn.className = "col-pin";
        btn.innerHTML = PIN_SVG;
        btn.style.cssText = "display:inline-flex;vertical-align:middle;margin-left:4px;cursor:pointer;";
        th.appendChild(btn);
        cleanups.push(() => btn.remove());
      }
      const active = pinned.includes(key);
      btn.style.color = active ? "#0f172a" : "#cbd5e1";
      btn.style.transform = active ? "rotate(45deg)" : "";
      btn.title = active ? "Sabitlemeyi kaldır" : "Sütunu sabitle";
      btn.setAttribute("data-testid", `col-pin-${key}`);
      btn.onclick = (e) => {
        e.stopPropagation();
        const next = active ? pinned.filter((k) => k !== key) : [...pinned, key];
        setPinned(next);
        try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      };
    });
    let left = 0;
    ths.forEach((th, i) => {
      if (!pinned.includes(label(th))) return;
      const stick = (el, head) => {
        el.style.position = "sticky";
        el.style.left = left + "px";
        el.style.zIndex = head ? "30" : "20";
        el.style.backgroundColor = "#fff";
        el.style.boxShadow = "inset -2px 0 0 #e2e8f0";
      };
      stick(th, true);
      rows.forEach((tr) => { const c = visibleCells(tr)[i]; if (c && c.colSpan === 1) stick(c, false); });
      left += th.offsetWidth;
    });
    return () => cleanups.forEach((f) => f());
  });
  return pinned;
};


export const ColumnsMenu = ({ columns, hidden, onChange, testid, order, onReorder }) => {
  const ordered = order ? order.map((k) => columns.find((c) => c.key === k)).filter(Boolean) : columns;
  const move = (idx, dir) => {
    const keys = ordered.map((c) => c.key);
    const j = idx + dir;
    if (j < 0 || j >= keys.length) return;
    [keys[idx], keys[j]] = [keys[j], keys[idx]];
    onReorder(keys);
  };
  return (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        data-testid={testid}
        className={`h-10 px-3 inline-flex items-center gap-2 text-xs font-semibold border transition-colors duration-150 ${hidden.length ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"}`}
      >
        <Columns3 size={14} /> Sütunlar{hidden.length > 0 ? ` (${hidden.length} gizli)` : ""}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="bg-white border-slate-200 rounded-none min-w-[240px] max-h-80 overflow-y-auto">
      {ordered.map((c, idx) => (
        <div key={c.key} className="flex items-center gap-0.5 pr-1">
          <DropdownMenuCheckboxItem
            className="flex-1"
            checked={!hidden.includes(c.key)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => onChange(hidden.includes(c.key) ? hidden.filter((x) => x !== c.key) : [...hidden, c.key])}
            data-testid={`${testid}-opt-${c.key}`}
          >
            {c.label}
          </DropdownMenuCheckboxItem>
          {onReorder && (
            <>
              <button
                type="button"
                disabled={idx === 0}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); move(idx, -1); }}
                data-testid={`${testid}-up-${c.key}`}
                title="Yukarı taşı"
                className="w-5 h-5 inline-flex items-center justify-center text-slate-400 hover:text-slate-900 disabled:opacity-20 transition-colors duration-150"
              >
                <ArrowUp size={12} />
              </button>
              <button
                type="button"
                disabled={idx === ordered.length - 1}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); move(idx, 1); }}
                data-testid={`${testid}-down-${c.key}`}
                title="Aşağı taşı"
                className="w-5 h-5 inline-flex items-center justify-center text-slate-400 hover:text-slate-900 disabled:opacity-20 transition-colors duration-150"
              >
                <ArrowDown size={12} />
              </button>
            </>
          )}
        </div>
      ))}
      {hidden.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onChange([])} data-testid={`${testid}-show-all`} className="text-xs font-semibold cursor-pointer">
            Tümünü göster
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
  );
};

export const MultiFilter = ({ label, options, selected, onChange, totalCount, testid, width = "w-[190px]" }) => {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const text = selected.length === 0
    ? `Tümü (${totalCount})`
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label || selected[0])
      : `${selected.length} seçili`;
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className={`h-9 ${width} px-3 inline-flex items-center justify-between gap-2 text-xs font-semibold border transition-colors duration-150 ${selected.length ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}
          >
            <span className="truncate">{text}</span>
            <ChevronDown size={13} className="shrink-0 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="bg-white border-slate-200 rounded-none min-w-[220px] max-h-72 overflow-y-auto">
          <DropdownMenuCheckboxItem checked={selected.length === 0} onSelect={(e) => e.preventDefault()} onCheckedChange={() => onChange([])} data-testid={`${testid}-opt-all`}>
            Tümü ({totalCount})
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          {options.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.value}
              checked={selected.includes(o.value)}
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => toggle(o.value)}
              data-testid={`${testid}-opt-${trFold(String(o.label)).replace(/\s+/g, "-")}`}
            >
              {o.label} ({o.count})
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const AreaFilterButtons = ({ options, selected, onChange, testid }) =>
  options.length === 0 ? null : (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1">Stand m²:</span>
      {options.map((o) => {
        const act = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(act ? selected.filter((x) => x !== o.value) : [...selected, o.value])}
            data-testid={`${testid}-${o.value}`}
            className={`h-8 px-3 text-xs font-semibold border transition-colors duration-150 ${act ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
          >
            {o.value.toLocaleString("tr-TR")} m² <span className={act ? "text-slate-300" : "text-slate-400"}>({o.count})</span>
          </button>
        );
      })}
      {selected.length > 0 && (
        <button type="button" onClick={() => onChange([])} data-testid={`${testid}-clear`} className="h-8 px-2 text-xs font-semibold text-slate-400 hover:text-slate-900 transition-colors duration-150">
          Temizle
        </button>
      )}
    </div>
  );

export const ProducerCell = ({ p, producers, onAssign, onAddNew, onEdit, idp = "producer" }) => (
  <div className="flex items-center gap-1.5">
  <Select
    value={p.producer_id || "__none__"}
    onValueChange={(v) => {
      if (v === "__add__") return onAddNew(p);
      onAssign(p, v === "__none__" ? "" : v);
    }}
  >
    <SelectTrigger
      data-testid={`${idp}-select-${p.id}`}
      className={`rounded-none h-8 w-[160px] text-xs font-semibold ${p.producer_id ? "bg-amber-500/10 text-amber-700 border-amber-500/30" : "bg-white text-slate-400 border-slate-200"}`}
    >
      <SelectValue placeholder="Atanmamış">{p.producer_id ? (p.producer_name || "Üretici") : "Atanmamış"}</SelectValue>
    </SelectTrigger>
    <SelectContent className="bg-white border-slate-200 rounded-none">
      <SelectItem value="__none__">Atanmamış</SelectItem>
      {[...producers].sort((a, b) => (a.name || a.contact_name || "").localeCompare(b.name || b.contact_name || "", "tr", { sensitivity: "base" })).map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.contact_name}</SelectItem>)}
      <SelectItem value="__add__" className="text-blue-600 font-semibold" data-testid={`${idp}-add-new-${p.id}`}>+ Üretici Ekle</SelectItem>
    </SelectContent>
  </Select>
  {p.producer_id && onEdit && (
    <button
      type="button"
      onClick={() => onEdit(p.producer_id)}
      data-testid={`${idp}-edit-${p.id}`}
      title="Üreticiyi düzenle"
      className="text-slate-300 hover:text-slate-700 shrink-0 transition-colors duration-150"
    >
      <Pencil size={13} />
    </button>
  )}
  </div>
);

export const ContactCell = ({ p, field, contacts, onAssign, onAddNew, idp }) => {
  const kind = field === "sem_id" ? "SEM" : "CM";
  const nameKey = field === "sem_id" ? "sem_name" : "cm_name";
  if (kind === "SEM" && p.zone === "Blue Zone")
    return <span className="text-slate-300 text-xs" data-testid={`${idp}-disabled-${p.id}`} title="Blue Zone'da SEM yok">—</span>;
  const opts = contacts.filter((c) => c.kind === kind && (c.zone || "Green Zone") === p.zone).sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr", { sensitivity: "base" }));
  return (
    <Select
      value={p[field] || "__none__"}
      onValueChange={(v) => {
        if (v === "__add__") return onAddNew?.(p, field);
        onAssign(p, field, v === "__none__" ? "" : v);
      }}
    >
      <SelectTrigger
        data-testid={`${idp}-select-${p.id}`}
        className={`rounded-none h-8 w-[150px] text-xs font-semibold ${p[field] ? "bg-cyan-500/10 text-cyan-700 border-cyan-500/30" : "bg-white text-slate-400 border-slate-200"}`}
      >
        <SelectValue placeholder="Atanmamış">{p[field] ? (p[nameKey] || kind) : "Atanmamış"}</SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-white border-slate-200 rounded-none">
        <SelectItem value="__none__">Atanmamış</SelectItem>
        {opts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        <SelectItem value="__add__" className="text-blue-600 font-semibold" data-testid={`${idp}-add-new-${p.id}`}>+ {kind} Ekle</SelectItem>
      </SelectContent>
    </Select>
  );
};

export const ContactQuickAddDialog = ({ open, onOpenChange, kind = "SEM", zone, createEndpoint = "/contacts", onCreated }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setName(""); setPhone(""); setEmail(""); }
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Ad soyad girin");
    if (email.trim() && !email.includes("@")) return toast.error("Geçerli bir e-posta girin");
    setBusy(true);
    try {
      const { data } = await api.post(createEndpoint, { kind, zone: zone || null, name: name.trim(), phone: phone.trim() || null, email: email.trim() || null });
      toast.success(`${kind} eklendi`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="contact-quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Yeni {kind} Ekle{zone ? ` — ${zone}` : ""}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Ad Soyad</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="contact-add-name" className="rounded-none bg-[#F5F7FA] border-slate-200" />
          </div>
          <div className="space-y-1.5">
            <Label>Telefon <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+90 532 4566767" data-testid="contact-add-phone" className="rounded-none bg-[#F5F7FA] border-slate-200 placeholder:text-slate-300" />
          </div>
          <div className="space-y-1.5">
            <Label>E-posta <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kisi@firma.com" data-testid="contact-add-email" className="rounded-none bg-[#F5F7FA] border-slate-200 placeholder:text-slate-300" />
          </div>
          <p className="text-xs text-slate-400">Eklenen kişi bu projeye otomatik atanır. Kişileri Ayarlar sayfasından yönetebilirsiniz.</p>
          <Button type="submit" disabled={busy} data-testid="contact-add-submit" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
            {busy ? "Ekleniyor..." : `${kind} Ekle`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ProducerEditDialog = ({ open, onOpenChange, producer, saveEndpoint = "/producers", onSaved }) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && producer) {
      setName(producer.contact_name || "");
      setCompany(producer.company_name && producer.company_name !== "Üretici" ? producer.company_name : (producer.name || ""));
      setPhone(producer.phone || "");
      setEmail(producer.contact_email || "");
    }
  }, [open, producer]);
  const submit = async (e) => {
    e.preventDefault();
    if (!company.trim()) return toast.error("Firma adı girin");
    if (phone.trim().length < 5) return toast.error("Telefon girin");
    if (email.trim() && !email.includes("@")) return toast.error("Geçerli bir e-posta girin");
    setBusy(true);
    try {
      const { data } = await api.patch(`${saveEndpoint}/${producer.id}`, { company: company.trim(), contact_name: name.trim() || null, phone: phone.trim(), contact_email: email.trim() || null });
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="producer-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Üreticiyi Düzenle</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Ad Soyad <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="producer-edit-name" className="rounded-none bg-[#F5F7FA] border-slate-200" />
          </div>
          <div className="space-y-1.5">
            <Label>Firma Adı</Label>
            <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="producer-edit-company" className="rounded-none bg-[#F5F7FA] border-slate-200" />
          </div>
          <div className="space-y-1.5">
            <Label>Telefon</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+90 532 4566767" data-testid="producer-edit-phone" className="rounded-none bg-[#F5F7FA] border-slate-200 placeholder:text-slate-300" />
          </div>
          <div className="space-y-1.5">
            <Label>E-posta <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kisi@firma.com" data-testid="producer-edit-email" className="rounded-none bg-[#F5F7FA] border-slate-200 placeholder:text-slate-300" />
          </div>
          <Button type="submit" disabled={busy} data-testid="producer-edit-submit" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
            {busy ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ProducerQuickAddDialog = ({ open, onOpenChange, createEndpoint = "/producers", onCreated }) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setName(""); setCompany(""); setPhone(""); setEmail(""); }
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!company.trim()) return toast.error("Firma adı girin");
    if (phone.trim().length < 5) return toast.error("Telefon girin");
    if (email.trim() && !email.includes("@")) return toast.error("Geçerli bir e-posta girin");
    setBusy(true);
    try {
      const { data } = await api.post(createEndpoint, { company: company.trim(), contact_name: name.trim() || null, phone: phone.trim(), contact_email: email.trim() || null });
      toast.success("Üretici eklendi");
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="producer-quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Yeni Üretici Ekle</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Ad Soyad <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="producer-add-name" className="rounded-none bg-[#F5F7FA] border-slate-200" />
          </div>
          <div className="space-y-1.5">
            <Label>Firma Adı</Label>
            <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="producer-add-company" className="rounded-none bg-[#F5F7FA] border-slate-200" />
          </div>
          <div className="space-y-1.5">
            <Label>Telefon</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+90 532 4566767" data-testid="producer-add-phone" className="rounded-none bg-[#F5F7FA] border-slate-200 placeholder:text-slate-300" />
          </div>
          <div className="space-y-1.5">
            <Label>E-posta <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kisi@firma.com" data-testid="producer-add-email" className="rounded-none bg-[#F5F7FA] border-slate-200 placeholder:text-slate-300" />
          </div>
          <p className="text-xs text-slate-400">Üretici yalnızca projelere atanan bir kayıttır; giriş hesabı oluşturulmaz, e-posta gönderilmez. Eklenen üretici bu projeye otomatik atanır.</p>
          <Button type="submit" disabled={busy} data-testid="producer-add-submit" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
            {busy ? "Ekleniyor..." : "Üretici Ekle"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ProductionStatusCell = ({ p, statuses, onSave, onAddNew, idp = "prod-status" }) => (
  <Select
    value={p.production_status || "__none__"}
    onValueChange={(v) => {
      if (v === "__add__") return onAddNew(p);
      onSave(p, v === "__none__" ? "" : v);
    }}
  >
    <SelectTrigger
      data-testid={`${idp}-select-${p.id}`}
      className={`rounded-none h-8 w-[170px] text-xs font-semibold ${prodStatusCls(p.production_status)}`}
    >
      <SelectValue placeholder="Atanmamış">{p.production_status || "Atanmamış"}</SelectValue>
    </SelectTrigger>
    <SelectContent className="bg-white border-slate-200 rounded-none">
      <SelectItem value="__none__">Atanmamış</SelectItem>
      {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
      <SelectItem value="__add__" className="text-blue-600 font-semibold" data-testid={`${idp}-add-new-${p.id}`}>+ Ekle</SelectItem>
    </SelectContent>
  </Select>
);

export const ProductionStatusAddDialog = ({ open, onOpenChange, onCreated }) => {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setVal(""); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!val.trim()) return toast.error("Durum adı girin");
    setBusy(true);
    try {
      const { data } = await api.post("/settings/production-statuses", { status: val.trim() });
      toast.success("Üretim durumu eklendi");
      onCreated?.(val.trim(), data.statuses);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-sm" data-testid="prod-status-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Yeni Üretim Durumu Ekle</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Durum Adı</Label>
            <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="örn. Sevkiyata Hazır" data-testid="prod-status-add-input" className="rounded-none bg-[#F5F7FA] border-slate-200" />
          </div>
          <Button type="submit" disabled={busy} data-testid="prod-status-add-submit" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
            {busy ? "Ekleniyor..." : "Ekle"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const SaleStatusAddDialog = ({ open, onOpenChange, onCreated }) => {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setVal(""); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!val.trim()) return toast.error("Durum adı girin");
    setBusy(true);
    try {
      const { data } = await api.post("/settings/sale-statuses", { status: val.trim() });
      setSaleStatusesCache(data.statuses);
      toast.success("Süreç durumu eklendi");
      onCreated?.(val.trim());
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-sm" data-testid="sale-status-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Yeni Süreç Durumu Ekle</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Durum Adı</Label>
            <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="örn. Revize Bekleniyor" data-testid="sale-status-add-input" className="rounded-none bg-[#F5F7FA] border-slate-200" />
          </div>
          <Button type="submit" disabled={busy} data-testid="sale-status-add-submit" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
            {busy ? "Ekleniyor..." : "Ekle"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ADMIN_COLS = [
  { key: "zone", label: "Bölge" }, { key: "stand_no", label: "Stand No" }, { key: "area", label: "m²" },
  { key: "type", label: "Tür" }, { key: "stand_type", label: "Stand Türü" }, { key: "tasarim", label: "Tasarım" },
  { key: "uretim", label: "Üretim" }, { key: "baski", label: "Baskı" }, { key: "elektrik", label: "Elektrik" },
  { key: "icmal", label: "İcmal" }, { key: "status", label: "Statü" }, { key: "sale", label: "Süreç Durum" },
  { key: "offer", label: "Teklif Tutarı" }, { key: "hub", label: "Hub" }, { key: "producer", label: "Üretici" }, { key: "sem", label: "SEM" }, { key: "cm", label: "CM" }, { key: "prod_status", label: "Üretim Durumu" }, { key: "requests", label: "Teklif Talebi" },
  { key: "winner", label: "Kazanan" }, { key: "detail", label: "Detay" },
];

export const InlineText = ({ value, onSave, testid, className = "", allowEmpty = false }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const commit = () => {
    setEditing(false);
    const v = val.trim();
    if (v === (value || "")) return;
    if (!v && !allowEmpty) return;
    onSave(v);
  };
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => { setVal(value || ""); setEditing(true); }}
        data-testid={testid}
        title="Düzenlemek için tıklayın"
        className={`text-left w-full cursor-text px-1 -mx-1 border border-transparent hover:border-slate-300 hover:bg-white transition-colors duration-150 ${className}`}
      >
        {value || <span className="text-slate-300">—</span>}
      </button>
    );
  }
  return (
    <Input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setVal(value || ""); setEditing(false); }
      }}
      data-testid={`${testid}-input`}
      className="rounded-none h-8 bg-white border-slate-400 text-sm px-2 min-w-[110px]"
    />
  );
};
const isPdfFile = (f) => (f.content_type || "") === "application/pdf" || (f.original_filename || "").toLowerCase().endsWith(".pdf");

const FilePreviewBody = ({ f }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  if (f.url && isImgFile(f)) {
    return <img src={f.url} alt={f.original_filename} className="w-full max-h-[70vh] object-contain border border-slate-100 bg-slate-50" />;
  }
  if (f.url && isPdfFile(f)) {
    return (
      <div className="space-y-3" data-testid="pdf-pager">
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={pageNum <= 1}
            onClick={() => setPageNum((n) => Math.max(1, n - 1))}
            data-testid="pdf-prev-page"
            className="w-8 h-8 inline-flex items-center justify-center border border-slate-300 text-slate-600 hover:bg-slate-900 hover:text-white hover:border-slate-900 disabled:opacity-30 disabled:pointer-events-none transition-colors duration-150"
          >
            <ChevronLeft size={15} />
          </button>
          <span className="text-xs font-semibold tabular-nums text-slate-600" data-testid="pdf-page-indicator">{pageNum} / {numPages || "…"}</span>
          <button
            type="button"
            disabled={numPages > 0 && pageNum >= numPages}
            onClick={() => setPageNum((n) => Math.min(numPages || n + 1, n + 1))}
            data-testid="pdf-next-page"
            className="w-8 h-8 inline-flex items-center justify-center border border-slate-300 text-slate-600 hover:bg-slate-900 hover:text-white hover:border-slate-900 disabled:opacity-30 disabled:pointer-events-none transition-colors duration-150"
          >
            <ChevronRight size={15} />
          </button>
        </div>
        <div className="flex justify-center bg-slate-100 border border-slate-200 py-4 max-h-[72vh] overflow-auto">
          <Document
            file={f.url}
            onLoadSuccess={({ numPages: n }) => { setNumPages(n); setPageNum(1); }}
            loading={<p className="text-slate-500 text-sm py-10">PDF yükleniyor...</p>}
            error={<p className="text-red-500 text-sm py-10">PDF görüntülenemedi. Lütfen dosyayı indirin.</p>}
          >
            <Page pageNumber={pageNum} width={Math.min(860, window.innerWidth - 180)} renderTextLayer={false} renderAnnotationLayer={false} className="shadow-lg" />
          </Document>
        </div>
      </div>
    );
  }
  return <div className="border border-dashed border-slate-200 p-4 text-xs text-slate-400">Bu dosya türü için önizleme yok — "Yeni sekmede aç" ile indirebilirsiniz.</div>;
};

export const DesignCell = ({ p, onChanged, endpoint, filesKey = "files", addKey = "add_file_ids", removeKey = "remove_file_ids", idp = "design", label = "Tasarım", pdfOnly = false, pdfImagesOnly = false, readOnly = false }) => {
  const ep = endpoint || `/projects/${p.id}`;
  const files = p[filesKey] || [];
  const sig = files.map((f) => f.id).join(",");
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(null);
  const [urls, setUrls] = useState(null);
  const prevSig = useRef(sig);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const addFiles = async (e) => {
    const list = e?.target?.files || e;
    if (!list || !list.length) return;
    const arr = Array.from(list);
    if (pdfOnly && arr.some((f) => f.type !== "application/pdf")) {
      toast.error("Yalnızca PDF dosyası yükleyebilirsiniz");
      if (e?.target) e.target.value = "";
      return;
    }
    const isArchive = (f) => /\.(zip|rar|7z)$/i.test(f.name) || ["application/zip", "application/x-zip-compressed", "application/x-rar-compressed", "application/vnd.rar", "application/x-7z-compressed"].includes(f.type);
    if (pdfImagesOnly && arr.some((f) => f.type !== "application/pdf" && !f.type.startsWith("image/") && !isArchive(f))) {
      toast.error("Yalnızca PDF, görsel veya ZIP/RAR yükleyebilirsiniz");
      if (e?.target) e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadSmart(arr, setProgress);
      const { data } = await api.patch(ep, { [addKey]: uploaded.map((f) => f.id) });
      onChanged?.(data[filesKey] || []);
      toast.success("Dosya(lar) eklendi");
    } catch (err) {
      toast.error(err?.message || apiError(err));
    } finally {
      setUploading(false);
      setProgress(null);
      if (e?.target) e.target.value = "";
    }
  };
  const load = async (force = false) => {
    if (urls && !force) return;
    const out = [];
    for (const f of files) {
      try {
        const { data } = await api.get(`/files/${f.id}/preview-url`);
        out.push({ ...f, url: data.url });
      } catch {
        out.push({ ...f, url: null });
      }
    }
    setUrls(out);
  };
  useEffect(() => {
    if (prevSig.current === sig) return;
    prevSig.current = sig;
    if (open) load(true);
    else setUrls(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, open]);
  const removeFile = async (f) => {
    if (!window.confirm(`"${f.original_filename}" dosyası projeden silinsin mi?`)) return;
    try {
      const { data } = await api.patch(ep, { [removeKey]: [f.id] });
      onChanged?.(data[filesKey] || []);
      toast.success("Dosya silindi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  if (files.length === 0) {
    return (
      <>
        <button
          type="button"
          onClick={() => { setOpen(true); setUrls([]); }}
          data-testid={`${idp}-attach-${p.id}`}
          title="Dosya ekle"
          className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-900 transition-colors duration-150"
        >
          <Paperclip size={15} />
          <span className="text-[10px] font-bold">0</span>
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-2xl" data-testid={`${idp}-modal-${p.id}`}>
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight text-base truncate pr-6">{p.name} — {label} Dosyaları</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-400">Henüz dosya yok.</p>
            {!readOnly && (
            <label
              {...dropProps(addFiles, setDragOver)}
              data-testid={`${idp}-dropzone-${p.id}`}
              className={`block border border-dashed px-4 py-6 text-center cursor-pointer transition-colors duration-150 ${dragOver ? "border-slate-900 bg-slate-100" : "border-slate-300 bg-slate-50 hover:border-slate-500"}`}
            >
              <input type="file" multiple className="hidden" onChange={addFiles} data-testid={`${idp}-upload-input-${p.id}`} accept={pdfOnly ? "application/pdf" : pdfImagesOnly ? "application/pdf,image/*,.zip,.rar,.7z" : undefined} />
              <div className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-500">
                <Upload size={14} /> Dosya eklemek için sürükleyin veya tıklayın
              </div>
            </label>
            )}
            {uploading && <UploadProgress progress={progress} />}
          </DialogContent>
        </Dialog>
      </>
    );
  }
  const firstImg = (urls || []).find((f) => isImgFile(f) && f.url);
  return (
    <>
      <HoverCard openDelay={150} open={hover && !open} onOpenChange={(o) => { setHover(o); if (o) load(); }}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            onClick={() => { setHover(false); setOpen(true); load(); }}
            data-testid={`${idp}-attach-${p.id}`}
            className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 transition-colors duration-150 cursor-pointer"
            title="Tasarımı önizle"
          >
            <Paperclip size={14} />
            <span className="text-[10px] font-bold">{files.length}</span>
          </button>
        </HoverCardTrigger>
        <HoverCardContent side="right" className="bg-white border-slate-200 rounded-none w-52 p-2" data-testid={`${idp}-hover-${p.id}`}>
          {!urls ? (
            <div className="h-24 flex items-center justify-center text-[10px] text-slate-400">Yükleniyor...</div>
          ) : firstImg ? (
            <img src={firstImg.url} alt="Tasarım" className="w-full h-28 object-cover border border-slate-100" />
          ) : (
            <div className="h-24 flex flex-col items-center justify-center gap-1 text-slate-400">
              <Paperclip size={18} />
              <span className="text-[10px]">Görsel önizleme yok</span>
            </div>
          )}
          <div className="text-[10px] text-slate-500 mt-1.5 truncate">
            {files[0].original_filename}{files.length > 1 ? ` +${files.length - 1} dosya` : ""}
          </div>
          <div className="text-[9px] text-slate-400 mt-0.5">Büyütmek için tıklayın</div>
        </HoverCardContent>
      </HoverCard>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-4xl max-h-[92vh] overflow-y-auto" data-testid={`${idp}-modal-${p.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{p.name} — {label} Önizleme</DialogTitle>
          </DialogHeader>
          {!urls ? (
            <div className="py-16 text-center text-sm text-slate-400">Yükleniyor...</div>
          ) : urls.length === 1 ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-600 truncate">{urls[0].original_filename}</span>
                <span className="flex items-center gap-3 shrink-0">
                  <button type="button" onClick={() => downloadFile(urls[0])} data-testid="preview-download-single" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors duration-150">
                    <Download size={13} /> İndir
                  </button>
                  {!readOnly && (
                  <button type="button" onClick={() => removeFile(urls[0])} data-testid={`${idp}-delete-single-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-600 transition-colors duration-150">
                    <Trash2 size={13} /> Sil
                  </button>
                  )}
                  {urls[0].url && <a href={urls[0].url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Yeni sekmede aç</a>}
                </span>
              </div>
              <FilePreviewBody f={urls[0]} />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${idp}-grid-${p.id}`}>
              {urls.map((f) => (
                <div
                  key={f.id}
                  data-testid={`${idp}-grid-item-${f.id}`}
                  className="text-left border border-slate-200 hover:border-slate-900 transition-colors duration-150 bg-white group"
                >
                  <button type="button" onClick={() => setActive(f)} className="block w-full text-left cursor-pointer">
                    {f.url && isImgFile(f) ? (
                      <img src={f.url} alt={f.original_filename} className="w-full h-36 object-cover border-b border-slate-100" />
                    ) : (
                      <div className="w-full h-36 flex flex-col items-center justify-center gap-1.5 bg-slate-50 border-b border-slate-100 text-slate-400">
                        <Paperclip size={22} />
                        <span className="text-[10px] font-semibold uppercase tracking-wider">{isPdfFile(f) ? "PDF" : "Dosya"}</span>
                      </div>
                    )}
                  </button>
                  <div className="px-2.5 py-2 flex items-center gap-1.5">
                    <button type="button" onClick={() => setActive(f)} className="min-w-0 flex-1 text-left cursor-pointer">
                      <div className="text-xs font-semibold text-slate-700 truncate group-hover:text-slate-900">{f.original_filename}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">Önizlemek için tıklayın</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadFile(f)}
                      title="İndir"
                      data-testid={`${idp}-grid-download-${f.id}`}
                      className="w-7 h-7 shrink-0 inline-flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors duration-150"
                    >
                      <Download size={14} />
                    </button>
                    {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeFile(f)}
                      title="Sil"
                      data-testid={`${idp}-grid-delete-${f.id}`}
                      className="w-7 h-7 shrink-0 inline-flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-150"
                    >
                      <Trash2 size={14} />
                    </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!readOnly && (
          <label
            {...dropProps(addFiles, setDragOver)}
            data-testid={`${idp}-dropzone-${p.id}`}
            className={`block border border-dashed px-4 py-5 text-center cursor-pointer transition-colors duration-150 ${dragOver ? "border-slate-900 bg-slate-100" : "border-slate-300 bg-slate-50 hover:border-slate-500"}`}
          >
            <input type="file" multiple className="hidden" onChange={addFiles} data-testid={`${idp}-upload-input-${p.id}`} accept={pdfOnly ? "application/pdf" : pdfImagesOnly ? "application/pdf,image/*,.zip,.rar,.7z" : undefined} />
            <div className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-500">
              <Upload size={14} /> Dosya eklemek için sürükleyin veya tıklayın
            </div>
          </label>
          )}
          {uploading && <UploadProgress progress={progress} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-4xl max-h-[92vh] overflow-y-auto" data-testid={`${idp}-file-modal-${p.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-base truncate pr-6">{active?.original_filename}</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={() => downloadFile(active)} data-testid="preview-download-file" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors duration-150">
                  <Download size={13} /> İndir
                </button>
                {active.url && <a href={active.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Yeni sekmede aç</a>}
              </div>
              <FilePreviewBody f={active} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [zoneSel, setZoneSel] = useState([]);
  const [stSel, setStSel] = useState([]);
  const [hubSel, setHubSel] = useState([]);
  const [saleSel, setSaleSel] = useState([]);
  const [standTypeSel, setStandTypeSel] = useState([]);
  const [producerSel, setProducerSel] = useState([]);
  const [prodStSel, setProdStSel] = useState([]);
  const [semSel, setSemSel] = useState([]);
  const [cmSel, setCmSel] = useState([]);
  const [areaSel, setAreaSel] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [producers, setProducers] = useState([]);
  const [prodAddFor, setProdAddFor] = useState(null);
  const [prodEditFor, setProdEditFor] = useState(null);
  const [prodStatuses, setProdStatuses] = useState([]);
  const [prodStatusAddFor, setProdStatusAddFor] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactAddFor, setContactAddFor] = useState(null);
  const [saleAddFor, setSaleAddFor] = useState(null);

  useEffect(() => {
    api.get("/settings/hubs").then((r) => setHubs(r.data.hubs || [])).catch(() => {});
    api.get("/producers/options").then((r) => setProducers(r.data || [])).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data || [])).catch(() => {});
    api.get("/settings/production-statuses").then((r) => setProdStatuses(r.data.statuses || [])).catch(() => {});
  }, []);
  const [loaded, setLoaded] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editArea, setEditArea] = useState("");
  const [editName, setEditName] = useState("");
  const [editZone, setEditZone] = useState("");
  const [editStand, setEditStand] = useState("");
  const [editType, setEditType] = useState("");
  const [editStandType, setEditStandType] = useState("");
  const [editFiles, setEditFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proposalFor, setProposalFor] = useState(null);

  const patchProject = async (p, payload, optimistic) => {
    try {
      await api.patch(`/projects/${p.id}`, payload);
      setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, ...optimistic } : x)));
      toast.success("Proje güncellendi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setProjectSale = async (p, v) => {
    const val = v === "__none__" ? "" : v;
    try {
      await api.patch(`/projects/${p.id}`, { sale_status: val });
      setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, sale_status: val || null } : x)));
      toast.success(val ? `${p.name} → ${val}` : `${p.name}: durum kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setProjectHub = async (p, v) => {
    const val = v === "__none__" ? "" : v;
    try {
      await api.patch(`/projects/${p.id}`, { hub: val });
      setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, hub: val || null } : x)));
      toast.success(val ? `${p.name} → ${val}` : `${p.name}: hub kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setProjectOffer = async (p, payload) => {
    try {
      await api.patch(`/projects/${p.id}`, payload);
      setProjects((ps) => ps.map((x) => (x.id === p.id ? {
        ...x,
        ...(payload.offer_amount !== undefined ? { offer_amount: payload.offer_amount > 0 ? payload.offer_amount : null } : {}),
        ...(payload.offer_currency !== undefined ? { offer_currency: payload.offer_currency } : {}),
      } : x)));
      toast.success(`${p.name}: teklif tutarı güncellendi`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setProjectStatus = async (p, v) => {
    const val = v === "__none__" ? "" : v;
    try {
      await api.patch(`/projects/${p.id}`, { project_status: val });
      setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, project_status: val || null } : x)));
      toast.success(val ? `${p.name} → ${val}` : `${p.name}: statü kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditArea(p.area_m2 ?? "");
    setEditName(p.name || "");
    setEditZone(p.zone || "");
    setEditStand(p.stand_no || "");
    setEditType(p.project_type || "");
    setEditStandType(p.stand_type || "");
    setEditFiles(p.files || []);
  };

  const syncProject = (patch) => setProjects((ps) => ps.map((x) => (x.id === editing.id ? { ...x, ...patch } : x)));

  const addEditFiles = async (e) => {
    const files = e?.target?.files || e;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = await uploadSmart(files, setProgress);
      const { data } = await api.patch(`/projects/${editing.id}`, { add_file_ids: uploaded.map((f) => f.id) });
      setEditFiles(data.files);
      syncProject({ files: data.files });
      toast.success("Dosya(lar) eklendi");
    } catch (err) {
      toast.error(err?.message || apiError(err));
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const removeEditFile = async (fileId) => {
    try {
      const { data } = await api.patch(`/projects/${editing.id}`, { remove_file_ids: [fileId] });
      setEditFiles(data.files);
      syncProject({ files: data.files });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (editName.trim() && editName.trim() !== editing.name) payload.name = editName.trim();
      if (editZone && editZone !== editing.zone) payload.zone = editZone;
      if (editStand.trim() !== (editing.stand_no || "")) payload.stand_no = editStand.trim();
      if (editType && editType !== editing.project_type) payload.project_type = editType;
      const newStandType = editType === "Fuar Standı" ? editStandType : "";
      if (newStandType !== (editing.stand_type || "")) payload.stand_type = newStandType;
      if (editArea !== "" && !isNaN(parseFloat(editArea))) payload.area_m2 = parseFloat(editArea);
      if (Object.keys(payload).length > 0) {
        const { data } = await api.patch(`/projects/${editing.id}`, payload);
        syncProject({ area_m2: data.area_m2, name: payload.name ?? editing.name, zone: payload.zone ?? editing.zone, stand_no: payload.stand_no ?? editing.stand_no, project_type: payload.project_type ?? editing.project_type, stand_type: payload.stand_type !== undefined ? (payload.stand_type || null) : editing.stand_type });
      }
      toast.success("Proje güncellendi");
      setEditing(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/projects/${toDelete.id}`);
      setProjects((ps) => ps.filter((x) => x.id !== toDelete.id));
      toast.success("Proje silindi");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setToDelete(null);
    }
  };

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const assignProducer = async (p, producerId, nameHint) => {
    try {
      await api.patch(`/projects/${p.id}`, { producer_id: producerId });
      const pname = producerId ? (nameHint || producers.find((u) => u.id === producerId)?.name || producers.find((u) => u.id === producerId)?.contact_name || "Üretici") : null;
      setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, producer_id: producerId || null, producer_name: pname } : x)));
      toast.success(producerId ? `${p.name} → ${pname}` : `${p.name}: üretici kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const assignContact = async (p, field, cid, newName) => {
    const nameKey = field === "sem_id" ? "sem_name" : "cm_name";
    try {
      await api.patch(`/projects/${p.id}`, { [field]: cid });
      const cname = newName || (cid ? contacts.find((c) => c.id === cid)?.name || null : null);
      setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, [field]: cid || null, [nameKey]: cname } : x)));
      toast.success(cid ? `${p.name} → ${cname}` : `${p.name}: atama kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setProductionStatus = async (p, val) => {
    try {
      await api.patch(`/projects/${p.id}`, { production_status: val });
      setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, production_status: val || null } : x)));
      toast.success(val ? `${p.name} → ${val}` : `${p.name}: üretim durumu kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const searched = projects.filter(
    (p) =>
      trFold(p.name).includes(trFold(search)) ||
      trFold(p.stand_no || "").includes(trFold(search)) ||
      trFold(p.zone || p.category || "").includes(trFold(search))
  );
  const TABS = [
    { key: "all", label: "Tümü", pred: () => true },
    { key: "active", label: "Aktif", pred: (p) => p.tender_state === "active" },
    { key: "closed", label: "Kapandı", pred: (p) => p.tender_state === "closed" },
    { key: "won", label: "Kazananlar", pred: (p) => p.tender_state === "won" },
  ];
  const tabDef = TABS.find((t) => t.key === tab) || TABS[0];
  const tabBase = searched.filter(tabDef.pred);
  const zoneOf = (p) => p.zone || p.category || "Diğer";
  const STAND_TYPES = ["Custom", "Template", "Customized Template"];
  const FM = {
    area: (p) => areaSel.length === 0 || areaSel.includes(areaNum(p.area_m2)),
    zone: (p) => zoneSel.length === 0 || zoneSel.includes(zoneOf(p)),
    hub: (p) => hubSel.length === 0 || hubSel.includes(p.hub || "__none__"),
    st: (p) => stSel.length === 0 || stSel.includes(p.project_status || "__none__"),
    sale: (p) => saleSel.length === 0 || saleSel.includes(p.sale_status || "__none__"),
    type: (p) => standTypeSel.length === 0 || standTypeSel.includes(p.stand_type || "__none__"),
    producer: (p) => producerSel.length === 0 || producerSel.includes(p.producer_id || "__none__"),
    prodSt: (p) => prodStSel.length === 0 || prodStSel.includes(p.production_status || "__none__"),
    sem: (p) => semSel.length === 0 || semSel.includes(p.sem_id || "__none__"),
    cm: (p) => cmSel.length === 0 || cmSel.includes(p.cm_id || "__none__"),
  };
  const baseFor = (except) => tabBase.filter((p) => Object.entries(FM).every(([k, fn]) => k === except || fn(p)));
  const areaFacet = baseFor("area");
  const areaOptions = [...new Set(areaFacet.map((p) => areaNum(p.area_m2)).filter((n) => n > 0))]
    .sort((a, b) => a - b)
    .map((n) => ({ value: n, count: areaFacet.filter((p) => areaNum(p.area_m2) === n).length }));
  const zoneFacet = baseFor("zone");
  const zones = [...new Set([...zoneFacet.map(zoneOf), ...zoneSel])].sort((a, b) => a.localeCompare(b, "tr"));
  const hubFacet = baseFor("hub");
  const stFacet = baseFor("st");
  const saleFacet = baseFor("sale");
  const typeFacet = baseFor("type");
  const producerFacet = baseFor("producer");
  const producerOpts = [...new Map(producerFacet.filter((p) => p.producer_id).map((p) => [p.producer_id, p.producer_name || "Üretici"])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], "tr", { sensitivity: "base" }));
  const prodStFacet = baseFor("prodSt");
  const semFacet = baseFor("sem");
  const semOpts = [...new Map(semFacet.filter((p) => p.sem_id).map((p) => [p.sem_id, p.sem_name || "SEM"])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], "tr", { sensitivity: "base" }));
  const cmFacet = baseFor("cm");
  const cmOpts = [...new Map(cmFacet.filter((p) => p.cm_id).map((p) => [p.cm_id, p.cm_name || "CM"])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], "tr", { sensitivity: "base" }));
  const filtered = baseFor(null);
  const hubOptions = zoneSel.length ? [...new Set(zoneSel.flatMap((z) => hubsForZone(hubs, z)))] : hubsForZone(hubs, null);
  const statusOptions = zoneSel.length ? [...new Set(zoneSel.flatMap((z) => statusesForZone(z)))] : ALL_STATUSES;

  const kpis = {
    total: filtered.length,
    area: filtered.reduce((s, p) => s + areaNum(p.area_m2), 0),
    supplier: filtered.filter((p) => p.winner).length,
    ozel: filtered.filter((p) => p.project_status === "Özel").length,
    sbb: filtered.filter((p) => p.project_status === "SBB").length,
  };

  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const saleStatuses = useSaleStatuses();
  const [hiddenCols, setHiddenCols] = useHiddenCols("cop31_admin_hidden_cols");
  const [colOrder, setColOrder] = useColOrder("cop31_admin_col_order", ADMIN_COLS.map((c) => c.key));
  const v = (k) => !hiddenCols.includes(k);
  const cls = (k, base) => (v(k) ? base : `${base} hidden`);
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortVal = (p) => {
    switch (sort.key) {
      case "zone": return p.zone || p.category || "";
      case "stand_no": return p.stand_no || "";
      case "area": return areaNum(p.area_m2);
      case "type": return p.project_type || "";
      case "status": return p.project_status || "";
      case "stand_type": return p.stand_type || "";
      case "tasarim": return (p.files || []).length;
      case "uretim": return (p.uretim_files || []).length;
      case "baski": return (p.baski_files || []).length;
      case "elektrik": return (p.elektrik_files || []).length;
      case "icmal": return (p.icmal_files || []).length;
      case "sale": return p.sale_status || "";
      case "offer": return p.offer_amount ?? -1;
      case "hub": return p.hub || "";
      case "producer": return p.producer_name || "";
      case "sem": return p.sem_name || "";
      case "cm": return p.cm_name || "";
      case "prod_status": return p.production_status || "";
      case "requests": return p.request_count || 0;
      case "winner": return p.winner ? Number(p.winner.price) || 0 : -1;
      default: return p.name || "";
    }
  };
  const sortedList = [...filtered].sort((a, b) => {
    const va = sortVal(a), vb = sortVal(b);
    const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "tr", { numeric: true, sensitivity: "base" });
    return sort.dir === "asc" ? cmp : -cmp;
  });
  const SortTh = ({ k, children, right, hide }) => hide ? null : (
    <th className={`px-4 py-3 font-medium ${right ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors duration-150 hover:text-slate-900 ${sort.key === k ? "text-slate-900" : ""}`}
      >
        {children}
        {sort.key === k ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="text-slate-300" />}
      </button>
    </th>
  );
  const showHubCol = zoneSel.length === 0 || zoneSel.some((z) => hubsForZone(hubs, z).length > 0);
  const tableRef = useRef(null);
  useResizableCols(tableRef, "cop31_admin_col_widths");
  usePinnedCols(tableRef, "cop31_admin_col_pins");

  const renderRow = (p, i) => (
    <tr key={p.id} className="border-b border-slate-200 last:border-b-0 hover:bg-white transition-colors duration-200" data-testid={`project-row-${p.id}`}>
      <td className="px-3 py-3 text-slate-400 tabular-nums text-xs" data-testid={`project-rownum-${p.id}`}>{i + 1}</td>
      <td className="px-4 py-3 font-medium">
        <span className="inline-flex items-center gap-2 w-full">
          <InlineText value={p.name} onSave={(v) => patchProject(p, { name: v }, { name: v })} testid={`inline-name-${p.id}`} className="font-medium" />
          {p.created_by_role === "assigner" && (
            <Badge variant="outline" className="rounded-none text-[10px] bg-violet-500/10 text-violet-600 border-violet-500/20" data-testid={`project-creator-badge-${p.id}`}>
              Ekleyen: {p.created_by_name}
            </Badge>
          )}
          {p.unread_messages > 0 && (
            <Badge className="rounded-none bg-blue-600 text-white text-[10px]" data-testid={`admin-unread-${p.id}`}>{p.unread_messages} yeni mesaj</Badge>
          )}
        </span>
      </td>
      <OrderedCells order={colOrder}>
      <td key="zone" className={cls("zone", "px-4 py-3")}>
        <Select value={p.zone || ""} onValueChange={(v) => patchProject(p, { zone: v }, { zone: v })}>
          <SelectTrigger
            data-testid={`inline-zone-${p.id}`}
            className={`rounded-none h-7 w-[118px] text-xs font-semibold border px-2 ${zoneCls[p.zone] || "border-slate-300 text-slate-600 bg-white"}`}
          >
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 rounded-none">
            {["Blue Zone", "Green Zone", "Ofisler"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
          </SelectContent>
        </Select>
        {p.source === "customer" && <Badge variant="outline" className="rounded-none text-[10px] mt-1 bg-blue-500/10 text-blue-600 border-blue-500/20">Müşteri</Badge>}
      </td>
      <td key="stand_no" className={cls("stand_no", "px-4 py-3 text-slate-600")}>
        <InlineText value={p.stand_no || p.location || ""} onSave={(v) => patchProject(p, { stand_no: v }, { stand_no: v })} testid={`inline-stand-${p.id}`} />
      </td>
      <td key="area" className={cls("area", "px-4 py-3 text-slate-600 tabular-nums")}>
        <InlineText
          value={p.area_m2 ? String(p.area_m2).replace(".", ",") : ""}
          onSave={(v) => {
            const n = parseFloat(String(v).replace(",", "."));
            if (!Number.isFinite(n) || n < 0) { toast.error("Geçerli bir m² değeri girin"); return; }
            patchProject(p, { area_m2: n }, { area_m2: n });
          }}
          testid={`inline-area-${p.id}`}
        />
      </td>
      <td key="type" className={cls("type", "px-4 py-3 text-slate-500")}>
        <Select value={p.project_type || ""} onValueChange={(v) => patchProject(p, { project_type: v }, { project_type: v, ...(v !== "Fuar Standı" ? { stand_type: null } : {}) })}>
          <SelectTrigger
            data-testid={`inline-type-${p.id}`}
            className="rounded-none h-7 w-[118px] text-xs border-slate-200 bg-white text-slate-600 px-2"
          >
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 rounded-none">
            {["Fuar Standı", "Etkinlik", "Teknik"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td key="stand_type" className={cls("stand_type", "px-4 py-3")}>
        {p.project_type === "Fuar Standı" ? (
          <Select value={p.stand_type || "__none__"} onValueChange={(v) => { const val = v === "__none__" ? "" : v; patchProject(p, { stand_type: val }, { stand_type: val || null }); }}>
            <SelectTrigger
              data-testid={`inline-stand-type-${p.id}`}
              className={`rounded-none h-7 w-[150px] text-xs px-2 ${p.stand_type ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-white text-slate-400 border-slate-200"}`}
            >
              <SelectValue placeholder="Atanmamış" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200 rounded-none">
              <SelectItem value="__none__">Atanmamış</SelectItem>
              {["Custom", "Template", "Customized Template"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td key="tasarim" className={cls("tasarim", "px-4 py-3")}>
        <DesignCell p={p} onChanged={(files) => setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, files, file_ids: files.map((f) => f.id) } : x)))} />
      </td>
      <td key="uretim" className={cls("uretim", "px-4 py-3")}>
        <DesignCell
          p={p}
          filesKey="uretim_files"
          addKey="add_uretim_file_ids"
          removeKey="remove_uretim_file_ids"
          idp="uretim"
          label="Üretim"
          pdfImagesOnly
          onChanged={(uretim_files) => setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, uretim_files } : x)))}
        />
      </td>
      <td key="baski" className={cls("baski", "px-4 py-3")}>
        <DesignCell
          p={p}
          filesKey="baski_files"
          addKey="add_baski_file_ids"
          removeKey="remove_baski_file_ids"
          idp="baski"
          label="Baskı"
          pdfImagesOnly
          onChanged={(baski_files) => setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, baski_files } : x)))}
        />
      </td>
      <td key="elektrik" className={cls("elektrik", "px-4 py-3")}>
        <DesignCell
          p={p}
          filesKey="elektrik_files"
          addKey="add_elektrik_file_ids"
          removeKey="remove_elektrik_file_ids"
          idp="elektrik"
          label="Elektrik"
          onChanged={(elektrik_files) => setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, elektrik_files } : x)))}
        />
      </td>
      <td key="icmal" className={cls("icmal", "px-4 py-3")}>
        <DesignCell
          p={p}
          filesKey="icmal_files"
          addKey="add_icmal_file_ids"
          removeKey="remove_icmal_file_ids"
          idp="icmal"
          label="İcmal"
          pdfOnly
          onChanged={(icmal_files) => setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, icmal_files } : x)))}
        />
      </td>
      <td key="status" className={cls("status", "px-4 py-3")}>
        <Select value={p.project_status || "__none__"} onValueChange={(v) => setProjectStatus(p, v)}>
          <SelectTrigger
            data-testid={`project-status-select-${p.id}`}
            className={`rounded-none h-8 w-[150px] text-xs font-semibold ${statusColorCls(p.project_status)}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 rounded-none">
            <SelectItem value="__none__">Atanmamış</SelectItem>
            {statusesForZone(p.zone).map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td key="sale" className={cls("sale", "px-4 py-3")}>
        <Select value={p.sale_status || "__none__"} onValueChange={(v) => (v === "__add__" ? setSaleAddFor(p) : setProjectSale(p, v))}>
          <SelectTrigger
            data-testid={`project-sale-select-${p.id}`}
            className={`rounded-none h-8 w-[170px] text-xs font-semibold ${saleStatusCls(p.sale_status)}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 rounded-none">
            <SelectItem value="__none__">Atanmamış</SelectItem>
            {saleStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            <SelectItem value="__add__" className="text-blue-600 font-semibold" data-testid={`sale-add-new-${p.id}`}>+ Ekle</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td key="offer" className={cls("offer", "px-4 py-3")}>
        <OfferAmountCell p={p} onSave={setProjectOffer} />
      </td>
      {showHubCol && v("hub") && (
      <td key="hub" className="px-4 py-3">
        {hubsForZone(hubs, p.zone).length === 0 && !p.hub ? (
          <span className="text-slate-300 text-xs">—</span>
        ) : (
        <Select value={p.hub || "__none__"} onValueChange={(v) => setProjectHub(p, v)}>
          <SelectTrigger
            data-testid={`project-hub-select-${p.id}`}
            className={`rounded-none h-8 w-[150px] text-xs font-semibold ${p.hub ? "border-slate-300 text-slate-700 bg-white" : "border-amber-300 text-amber-700 bg-amber-50"}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 rounded-none">
            <SelectItem value="__none__">Atanmamış</SelectItem>
            {hubsForZone(hubs, p.zone).map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
          </SelectContent>
        </Select>
        )}
      </td>
      )}
      <td key="producer" className={cls("producer", "px-4 py-3")}>
        <ProducerCell p={p} producers={producers} onAssign={assignProducer} onAddNew={(proj) => setProdAddFor(proj)} onEdit={(pid) => { const u = producers.find((x) => x.id === pid); if (u) setProdEditFor(u); }} idp="producer" />
      </td>
      <td key="sem" className={cls("sem", "px-4 py-3")}>
        <ContactCell p={p} field="sem_id" contacts={contacts} onAssign={assignContact} onAddNew={(proj, field) => setContactAddFor({ p: proj, field })} idp="sem" />
      </td>
      <td key="cm" className={cls("cm", "px-4 py-3")}>
        <ContactCell p={p} field="cm_id" contacts={contacts} onAssign={assignContact} onAddNew={(proj, field) => setContactAddFor({ p: proj, field })} idp="cm" />
      </td>
      <td key="prod_status" className={cls("prod_status", "px-4 py-3")}>
        <ProductionStatusCell p={p} statuses={prodStatuses} onSave={setProductionStatus} onAddNew={(proj) => setProdStatusAddFor(proj)} idp="prod-status" />
      </td>
      <td key="requests" className={cls("requests", "px-4 py-3 text-right")}>
        <span className="font-heading font-bold">{p.request_count}</span>
      </td>
      <td key="winner" className={cls("winner", "px-4 py-3")} data-testid={`project-winner-${p.id}`}>
        {p.winner ? (
          <span className="inline-flex items-center gap-2">
            <span className="inline-flex flex-col">
              <span className="font-semibold text-emerald-700 text-xs">{p.winner.company}</span>
              <span className="font-heading font-bold tabular-nums">{Number(p.winner.price).toLocaleString("tr-TR")} {p.winner.currency}</span>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setProposalFor(p)}
              data-testid={`send-proposal-${p.id}`}
              className="rounded-none h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1 text-xs transition-colors duration-200"
              title="Bu proje için teklif gönder (PDF ekiyle)"
            >
              <Send size={12} /> Fiyat Önerisi
            </Button>
          </span>
        ) : p.manufacturer?.company ? (
          <span className="inline-flex flex-col" data-testid={`project-manufacturer-${p.id}`}>
            <span className="font-semibold text-amber-700 text-xs">{p.manufacturer.company}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Üretici</span>
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td key="detail" className={cls("detail", "px-4 py-3 text-right")}>
        <Link to={`/admin/projeler/${p.id}`} data-testid={`project-detail-link-${p.id}`}>
          <Button variant="outline" size="sm" className="rounded-none h-8 border-slate-200 hover:bg-slate-100 transition-colors duration-200" title="Detay">
            <Eye size={13} />
          </Button>
        </Link>
      </td>
      </OrderedCells>
      <td className="px-4 py-3 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid={`project-actions-${p.id}`}
              className="rounded-none h-8 border-slate-200 hover:bg-slate-100 transition-colors duration-200" title="İşlemler">
              <MoreHorizontal size={14} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white border-slate-200 rounded-none min-w-[140px]">
            <DropdownMenuItem onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`} className="gap-2 text-sm cursor-pointer">
              <Pencil size={13} /> Düzenle
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setToDelete(p)} data-testid={`delete-project-${p.id}`} className="gap-2 text-sm cursor-pointer text-red-600 focus:text-red-600">
              <Trash2 size={13} /> Sil
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const rows = sortedList.map((p, i) => ({
      "#": i + 1,
      "Proje Adı": p.name || "",
      "Bölge": p.zone || p.category || "",
      "Stand No": p.stand_no || p.location || "",
      "Tür": p.project_type || "",
      "Stand Türü": p.stand_type || "",
      "Statü": p.project_status || "",
      "Süreç Durum": p.sale_status || "",
      "Teklif Tutarı": p.offer_amount != null ? `${Number(p.offer_amount).toLocaleString("tr-TR")} ${p.offer_currency || "TRY"}` : "",
      "Hub": p.hub || "",
      "Üretici": p.producer_name || "",
      "SEM": p.sem_name || "",
      "CM": p.cm_name || "",
      "Üretim Durumu": p.production_status || "",
      "Alan (m²)": areaNum(p.area_m2) || "",
      "Teklif Talebi": p.request_count || 0,
      "Kazanan": p.winner ? p.winner.company || "" : (p.manufacturer?.company ? `${p.manufacturer.company} (Üretici)` : ""),
      "Kazanan Tutar": p.winner ? `${Number(p.winner.price).toLocaleString("tr-TR")} ${p.winner.currency || ""}`.trim() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 4 }, { wch: 40 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 22 }, { wch: 10 }, { wch: 12 }, { wch: 24 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Projeler");
    XLSX.writeFile(wb, `projeler-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${rows.length} proje Excel'e aktarıldı`);
  };

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Projeler</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Tüm Projeler</h1>
        </div>
        <div className="flex items-center gap-2">
          <ColumnsMenu columns={ADMIN_COLS} hidden={hiddenCols} onChange={setHiddenCols} order={colOrder} onReorder={setColOrder} testid="projects-columns-menu" />
          <Button
            variant="outline"
            onClick={exportExcel}
            data-testid="projects-export-excel"
            className="rounded-none border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200"
          >
            <FileSpreadsheet size={16} /> Excel'e Aktar
          </Button>
          <Link to="/admin/proje-olustur" data-testid="go-create-project">
            <Button className="rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
              <Plus size={16} /> Proje Oluştur
            </Button>
          </Link>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="projects-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Proje, stand no veya bölge ara..."
          className="rounded-none bg-white border-slate-200 pl-9 pr-9"
        />
        <SearchClear value={search} onClear={() => setSearch("")} testid="projects-search-clear" />
      </div>

      <div className="space-y-3">
        <div className="flex border-b border-slate-200 overflow-x-auto" data-testid="projects-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`projects-tab-${t.key}`}
              className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 whitespace-nowrap transition-colors duration-200 ${
                tab === t.key
                  ? t.key === "won" ? "border-emerald-600 text-emerald-700" : "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-400 hover:text-slate-600"
              }`}
            >
              {t.label} ({searched.filter(t.pred).length})
            </button>
          ))}
        </div>
        <div className="flex items-end gap-3 flex-wrap" data-testid="projects-filters">
          {zones.length > 1 && (
            <MultiFilter
              label="Bölge"
              testid="projects-zone-select"
              totalCount={zoneFacet.length}
              selected={zoneSel}
              onChange={(v) => { setZoneSel(v); setHubSel([]); setStSel([]); }}
              options={zones.map((z) => ({ value: z, label: z, count: zoneFacet.filter((p) => zoneOf(p) === z).length }))}
            />
          )}
          {hubOptions.length > 0 && (
            <MultiFilter
              label="Hub"
              testid="projects-hub-select"
              width="w-[200px]"
              totalCount={hubFacet.length}
              selected={hubSel}
              onChange={setHubSel}
              options={[
                ...hubOptions.map((h) => ({ value: h, label: h, count: hubFacet.filter((p) => p.hub === h).length })),
                { value: "__none__", label: "Atanmamış", count: hubFacet.filter((p) => !p.hub).length },
              ]}
            />
          )}
          <MultiFilter
            label="Statü"
            testid="projects-status-select"
            width="w-[180px]"
            totalCount={stFacet.length}
            selected={stSel}
            onChange={setStSel}
            options={[
              ...statusOptions.map((st) => ({ value: st, label: st, count: stFacet.filter((p) => p.project_status === st).length })),
              { value: "__none__", label: "Atanmamış", count: stFacet.filter((p) => !p.project_status).length },
            ]}
          />
          <MultiFilter
            label="Süreç Durum"
            testid="projects-sale-select"
            width="w-[200px]"
            totalCount={saleFacet.length}
            selected={saleSel}
            onChange={setSaleSel}
            options={[
              ...saleStatuses.map((s) => ({ value: s, label: s, count: saleFacet.filter((p) => p.sale_status === s).length })),
              { value: "__none__", label: "Atanmamış", count: saleFacet.filter((p) => !p.sale_status).length },
            ]}
          />
          <MultiFilter
            label="Stand Türü"
            testid="projects-stand-type-select"
            totalCount={typeFacet.length}
            selected={standTypeSel}
            onChange={setStandTypeSel}
            options={[
              ...STAND_TYPES.map((t) => ({ value: t, label: t, count: typeFacet.filter((p) => p.stand_type === t).length })),
              { value: "__none__", label: "Atanmamış", count: typeFacet.filter((p) => !p.stand_type).length },
            ]}
          />
          <MultiFilter
            label="Üretici"
            testid="projects-producer-filter"
            width="w-[190px]"
            totalCount={producerFacet.length}
            selected={producerSel}
            onChange={setProducerSel}
            options={[
              ...producerOpts.map(([id, name]) => ({ value: id, label: name, count: producerFacet.filter((p) => p.producer_id === id).length })),
              { value: "__none__", label: "Atanmamış", count: producerFacet.filter((p) => !p.producer_id).length },
            ]}
          />
          <MultiFilter
            label="Üretim Durumu"
            testid="projects-prod-status-filter"
            width="w-[190px]"
            totalCount={prodStFacet.length}
            selected={prodStSel}
            onChange={setProdStSel}
            options={[
              ...prodStatuses.map((s) => ({ value: s, label: s, count: prodStFacet.filter((p) => p.production_status === s).length })),
              { value: "__none__", label: "Atanmamış", count: prodStFacet.filter((p) => !p.production_status).length },
            ]}
          />
          {semOpts.length > 0 && (
            <MultiFilter
              label="SEM"
              testid="projects-sem-filter"
              width="w-[190px]"
              totalCount={semFacet.length}
              selected={semSel}
              onChange={setSemSel}
              options={[
                ...semOpts.map(([id, name]) => ({ value: id, label: name, count: semFacet.filter((p) => p.sem_id === id).length })),
                { value: "__none__", label: "Atanmamış", count: semFacet.filter((p) => !p.sem_id).length },
              ]}
            />
          )}
          {cmOpts.length > 0 && (
            <MultiFilter
              label="CM"
              testid="projects-cm-filter"
              width="w-[190px]"
              totalCount={cmFacet.length}
              selected={cmSel}
              onChange={setCmSel}
              options={[
                ...cmOpts.map(([id, name]) => ({ value: id, label: name, count: cmFacet.filter((p) => p.cm_id === id).length })),
                { value: "__none__", label: "Atanmamış", count: cmFacet.filter((p) => !p.cm_id).length },
              ]}
            />
          )}
          {(search.trim() !== "" || zoneSel.length > 0 || hubSel.length > 0 || stSel.length > 0 || saleSel.length > 0 || standTypeSel.length > 0 || producerSel.length > 0 || prodStSel.length > 0 || semSel.length > 0 || cmSel.length > 0 || areaSel.length > 0) && (
            <button
              onClick={() => { setSearch(""); setZoneSel([]); setHubSel([]); setStSel([]); setSaleSel([]); setStandTypeSel([]); setProducerSel([]); setProdStSel([]); setSemSel([]); setCmSel([]); setAreaSel([]); }}
              data-testid="projects-filters-clear"
              className="h-9 px-3 text-xs font-semibold text-slate-500 hover:text-slate-900 border border-transparent hover:border-slate-300 transition-colors duration-200"
            >
              Filtreleri temizle
            </button>
          )}
        </div>
        <AreaFilterButtons options={areaOptions} selected={areaSel} onChange={setAreaSel} testid="projects-area-filter" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="projects-kpi-cards">
        <KpiCard icon={FolderOpen} label="Toplam Proje" value={kpis.total} accent="bg-slate-900 text-white" testid="kpi-total-projects" />
        <KpiCard icon={Ruler} label="Toplam m²" value={kpis.area.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} accent="bg-amber-500/10 text-amber-600" testid="kpi-total-area" />
        <KpiCard icon={UserCheck} label="Tedarikçi Atandı" value={kpis.supplier} accent="bg-violet-500/10 text-violet-600" testid="kpi-supplier-assigned" />
        <KpiCard icon={Box} label="Özel Stand" value={kpis.ozel} accent="bg-emerald-500/10 text-emerald-600" testid="kpi-ozel-stands" active={stSel.length === 1 && stSel[0] === "Özel"} onClick={() => setStSel(stSel.length === 1 && stSel[0] === "Özel" ? [] : ["Özel"])} />
        <KpiCard icon={Boxes} label="SBB" value={kpis.sbb} accent="bg-blue-500/10 text-blue-600" testid="kpi-sbb-stands" active={stSel.length === 1 && stSel[0] === "SBB"} onClick={() => setStSel(stSel.length === 1 && stSel[0] === "SBB" ? [] : ["SBB"])} />
      </div>

      <ProducerQuickAddDialog
        open={!!prodAddFor}
        onOpenChange={(o) => !o && setProdAddFor(null)}
        onCreated={(u) => { setProducers((ps) => [...ps, u]); if (prodAddFor) assignProducer(prodAddFor, u.id, u.name || u.contact_name); setProdAddFor(null); }}
      />

      <ProducerEditDialog
        open={!!prodEditFor}
        onOpenChange={(o) => !o && setProdEditFor(null)}
        producer={prodEditFor}
        saveEndpoint="/producers"
        onSaved={(u) => { setProducers((ps) => ps.map((x) => (x.id === u.id ? u : x))); setProjects((ps) => ps.map((x) => (x.producer_id === u.id ? { ...x, producer_name: u.name } : x))); setProdEditFor(null); toast.success("Üretici güncellendi"); }}
      />

      <ProductionStatusAddDialog
        open={!!prodStatusAddFor}
        onOpenChange={(o) => !o && setProdStatusAddFor(null)}
        onCreated={(st, list) => { setProdStatuses(list); if (prodStatusAddFor) setProductionStatus(prodStatusAddFor, st); setProdStatusAddFor(null); }}
      />

      <ContactQuickAddDialog
        open={!!contactAddFor}
        onOpenChange={(o) => !o && setContactAddFor(null)}
        kind={contactAddFor?.field === "cm_id" ? "CM" : "SEM"}
        zone={contactAddFor?.p?.zone}
        onCreated={(c) => { setContacts((cs) => [...cs, c]); if (contactAddFor) assignContact(contactAddFor.p, contactAddFor.field, c.id, c.name); setContactAddFor(null); }}
      />

      <SaleStatusAddDialog
        open={!!saleAddFor}
        onOpenChange={(o) => !o && setSaleAddFor(null)}
        onCreated={(st) => { if (saleAddFor) setProjectSale(saleAddFor, st); setSaleAddFor(null); }}
      />

      <div className="border border-slate-200 overflow-x-auto">
        <table ref={tableRef} className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-3 py-3 font-medium">#</th>
              <SortTh key="name" k="name">Proje Adı</SortTh>
              <OrderedCells order={colOrder}>
              <SortTh key="zone" k="zone" hide={!v("zone")}>Bölge</SortTh>
              <SortTh key="stand_no" k="stand_no" hide={!v("stand_no")}>Stand No</SortTh>
              <SortTh key="area" k="area" hide={!v("area")}>m²</SortTh>
              <SortTh key="type" k="type" hide={!v("type")}>Tür</SortTh>
              <SortTh key="stand_type" k="stand_type" hide={!v("stand_type")}>Stand Türü</SortTh>
              <SortTh key="tasarim" k="tasarim" hide={!v("tasarim")}>Tasarım</SortTh>
              <SortTh key="uretim" k="uretim" hide={!v("uretim")}>Üretim</SortTh>
              <SortTh key="baski" k="baski" hide={!v("baski")}>Baskı</SortTh>
              <SortTh key="elektrik" k="elektrik" hide={!v("elektrik")}>Elektrik</SortTh>
              <SortTh key="icmal" k="icmal" hide={!v("icmal")}>İcmal</SortTh>
              <SortTh key="status" k="status" hide={!v("status")}>Statü</SortTh>
              <SortTh key="sale" k="sale" hide={!v("sale")}>Süreç Durum</SortTh>
              <SortTh key="offer" k="offer" hide={!v("offer")}>Teklif Tutarı</SortTh>
              {showHubCol && <SortTh key="hub" k="hub" hide={!v("hub")}>Hub</SortTh>}
              <SortTh key="producer" k="producer" hide={!v("producer")}>Üretici</SortTh>
              <SortTh key="sem" k="sem" hide={!v("sem")}>SEM</SortTh>
              <SortTh key="cm" k="cm" hide={!v("cm")}>CM</SortTh>
              <SortTh key="prod_status" k="prod_status" hide={!v("prod_status")}>Üretim Durumu</SortTh>
              <SortTh key="requests" k="requests" right hide={!v("requests")}>Teklif Talebi</SortTh>
              <SortTh key="winner" k="winner" hide={!v("winner")}>Kazanan</SortTh>
              {v("detail") && <th key="detail" className="px-4 py-3 font-medium text-right">Detay</th>}
              </OrderedCells>
              <th className="px-4 py-3 font-medium text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={showHubCol ? 23 : 22} className="px-4 py-10 text-center text-slate-500" data-testid="projects-empty">
                  {!loaded ? "Yükleniyor..." : projects.length === 0 ? "Henüz proje yok. İlk projeyi oluşturun." : "Bu filtrelerle eşleşen proje bulunamadı."}
                </td>
              </tr>
            ) : (
              sortedList.map((p, i) => renderRow(p, i))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !uploading) setEditing(null); }}>
        <DialogContent aria-describedby={undefined} className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto bg-white" data-testid="edit-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{editing?.name} — Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label>Proje Adı</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                data-testid="edit-project-name-input"
                className="rounded-none bg-white border-slate-200 h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Bölge</Label>
                <Select value={editZone} onValueChange={setEditZone}>
                  <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="edit-project-zone-select">
                    <SelectValue placeholder="Bölge seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    {["Blue Zone", "Green Zone", "Ofisler"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Stand No</Label>
                <Input
                  value={editStand}
                  onChange={(e) => setEditStand(e.target.value)}
                  data-testid="edit-project-stand-input"
                  className="rounded-none bg-white border-slate-200 h-11"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Tür</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="edit-project-type-select">
                  <SelectValue placeholder="Tür seçin" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  {["Fuar Standı", "Etkinlik", "Teknik"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editType === "Fuar Standı" && (
              <div className="space-y-1.5">
                <Label>Stand Türü</Label>
                <Select value={editStandType || "__none__"} onValueChange={(v) => setEditStandType(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="edit-project-stand-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    <SelectItem value="__none__">Atanmamış</SelectItem>
                    {["Custom", "Template", "Customized Template"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Statü</Label>
              <Select value={editing?.project_status || "__none__"} onValueChange={(v) => editing && setProjectStatus(editing, v).then(() => setEditing((e) => (e ? { ...e, project_status: v === "__none__" ? null : v } : e)))}>
                <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="edit-project-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  <SelectItem value="__none__">Atanmamış</SelectItem>
                  {statusesForZone(editZone).map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hubsForZone(hubs, editZone).length > 0 && (
            <div className="space-y-1.5">
              <Label>Hub</Label>              <Select value={editing?.hub || "__none__"} onValueChange={(v) => editing && setProjectHub(editing, v).then(() => setEditing((e) => (e ? { ...e, hub: v === "__none__" ? null : v } : e)))}>
                <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="edit-project-hub-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  <SelectItem value="__none__">Atanmamış</SelectItem>
                  {hubsForZone(hubs, editZone).map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            <div className="space-y-1.5">
              <Label>Durum</Label>
              <Select value={editing?.sale_status || "__none__"} onValueChange={(v) => editing && setProjectSale(editing, v).then(() => setEditing((e) => (e ? { ...e, sale_status: v === "__none__" ? null : v } : e)))}>
                <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="edit-project-sale-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  <SelectItem value="__none__">Atanmamış</SelectItem>
                  {saleStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Alan (m²)</Label>
              <Input
                type="number"
                min="0"
                value={editArea}
                onChange={(e) => setEditArea(e.target.value)}
                placeholder="örn. 120"
                data-testid="edit-area-input"
                className="rounded-none bg-white border-slate-200 h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Proje Dosyaları</Label>
              <div
                {...dropProps(addEditFiles, setDragOver)}
                onClick={() => document.getElementById("edit-project-file-input")?.click()}
                data-testid="edit-project-dropzone"
                className={`border border-dashed p-5 flex flex-col items-center gap-2 text-slate-500 cursor-pointer transition-colors duration-200 ${
                  dragOver ? "border-slate-900 bg-slate-100 text-slate-900" : "border-slate-300 bg-[#F5F7FA] hover:border-slate-400"
                }`}
              >
                <Paperclip size={16} />
                <span className="text-xs">{uploading ? "Yükleniyor..." : dragOver ? "Dosyaları buraya bırakın" : "Dosya eklemek için tıklayın veya sürükleyip bırakın"}</span>
              </div>
              <input id="edit-project-file-input" type="file" multiple className="hidden" onChange={addEditFiles} data-testid="edit-project-file-input" />
              {uploading && <UploadProgress progress={progress} />}
              {editFiles.length > 0 && <FileChips files={editFiles} onRemove={removeEditFile} grid />}
            </div>
            <Button onClick={saveEdit} disabled={saving || uploading} data-testid="edit-project-save" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 h-11 font-semibold">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Projeyi sil</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              <span className="text-slate-900 font-medium">{toDelete?.name}</span> projesini kalıcı olarak silmek istediğinize emin misiniz?
              {toDelete?.request_count > 0 && <> Bu projeye bağlı <span className="text-slate-900 font-medium">{toDelete.request_count} teklif talebi</span> silinmez, listede kalmaya devam eder.</>} Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-200 hover:bg-slate-100" data-testid="project-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-none bg-red-600 hover:bg-red-500 text-white" data-testid="project-delete-confirm">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ProposalDialog project={proposalFor} onClose={() => setProposalFor(null)} />
    </div>
  );
}
